import React from 'react'
import { Row, Col } from 'react-grid-system'

import Layout from '../Layout'
import PageHeading from '../PageHeading'

import styles from './TermsAndConditions.module.scss'

const TermsAndConditions = () => {
  return (
    <Layout>
      <Row>
        <Col>
          <PageHeading>
            <h1>Terms and Conditions</h1>
          </PageHeading>

          <div className={styles.policy}>
            <div className={styles.header}>
              <p>
                <b>Buygoon Inc.</b>
              </p>
              <p>Effective Date: May 17, 2026 &bull; www.buygoon.com</p>
              <p>Address: 342 Queen St W, Suite 200, Toronto, ON M5V 2A2</p>
              <p>
                Legal Notices: <a href="mailto:legal@buygoon.com">legal@buygoon.com</a> &bull;
                Governing Law: Province of Ontario, Canada
              </p>
            </div>

            <p>
              IMPORTANT NOTICE: You must be at least 18 years of age to register for or use Buygoon.
              See Section 4 for full eligibility requirements.
            </p>

            <p>
              PLEASE NOTE THAT BUYGOON MAKES NO PROMISE OF ANY PARTICULAR AMOUNT OF MONEY TO BE
              SAVED OR EARNED THROUGH USE OF OUR PLATFORM. OUR PLATFORM IS OFFERED AS A FREE TOOL
              FOR YOU TO USE AS MUCH OR AS LITTLE AS YOU WISH. SEE SECTION 18.6 BELOW FOR FULL
              DETAILS.
            </p>

            <h2>1. Preamble and Acceptance of Terms</h2>

            <p>
              Please read these Terms and Conditions (&ldquo;Terms&rdquo; or &ldquo;Agreement&rdquo;)
              carefully before accessing or using the Buygoon platform. This Agreement is a legally
              binding contract between you (&ldquo;Member&rdquo; or &ldquo;you&rdquo;) and Buygoon
              Inc. (&ldquo;Buygoon,&rdquo; &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;),
              a corporation incorporated under the laws of Canada, with its registered address at
              342 Queen St W, Suite 200, Toronto, ON M5V 2A2, operating at www.buygoon.com.
            </p>

            <p>
              BY CLICKING &ldquo;ACCEPT ALL &amp; ENTER BUYGOON&rdquo; ON THE COOKIE WALL,
              REGISTERING FOR AN ACCOUNT, OR ACCESSING OR USING THE BUYGOON PLATFORM IN ANY WAY,
              YOU AGREE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY ALL OF THE TERMS
              AND CONDITIONS OF THIS AGREEMENT AND BY BUYGOON&rsquo;S PRIVACY POLICY, WHICH IS
              INCORPORATED HEREIN BY REFERENCE IN ITS ENTIRETY. IF YOU DO NOT AGREE TO THESE TERMS
              AND CONDITIONS IN THEIR ENTIRETY, DO NOT CLICK ACCEPT, DO NOT REGISTER, AND DO NOT
              ACCESS OR USE THE BUYGOON PLATFORM.
            </p>

            <p>
              This Agreement also contains mandatory individual arbitration and class action waiver
              provisions described in Section 22. Please review Section 22 carefully before
              accepting these Terms. These provisions significantly affect your legal rights,
              including your right to bring or participate in any class action or representative
              proceeding.
            </p>

            <p>
              This Agreement is governed by the laws of the Province of Ontario and the applicable
              federal laws of Canada, without regard to conflict of law principles of any
              jurisdiction whatsoever.
            </p>

            <h2>2. Definitions</h2>

            <p>
              The following defined terms are used throughout this Agreement. Additional terms may
              be defined in context.
            </p>

            <ul>
              <li>
                &ldquo;Affiliate Commission&rdquo; means the commission paid by a Merchant Retailer
                to an Affiliate Network Partner and credited in part by that Affiliate Network
                Partner to Buygoon as consideration for referring a Member to that Merchant
                Retailer.
              </li>
              <li>
                &ldquo;Affiliate Network Partners&rdquo; means Commission Junction (CJ Affiliate),
                Impact.com, and any other affiliate network through which Buygoon facilitates
                Cashback attribution, as updated from time to time.
              </li>
              <li>
                &ldquo;Agreement&rdquo; or &ldquo;Terms&rdquo; means these Terms and Conditions,
                together with Buygoon&rsquo;s Privacy Policy, as each may be amended from time to
                time.
              </li>
              <li>
                &ldquo;Buygoon Bonus Cash&rdquo; means the proprietary referral rewards currency of
                Buygoon Inc., earned by Members through the four-level referral rewards program
                described in Section 7. &ldquo;Buygoon Bonus Cash&rdquo; is a defined term and the
                exclusive intellectual property of Buygoon Inc. No other variant of this name has
                any meaning under this Agreement.
              </li>
              <li>
                &ldquo;Cashback&rdquo; means the portion of an Affiliate Commission credited to a
                Member&rsquo;s account as a reward for completing a qualifying purchase through the
                Platform.
              </li>
              <li>
                &ldquo;Confirmed Balance&rdquo; means an amount standing in a Member&rsquo;s account
                that has been verified by the applicable Affiliate Network Partner as a final,
                irreversible Affiliate Commission deposit into Buygoon&rsquo;s account. A Confirmed
                Balance represents a Contingent Right to Payment only &mdash; it is not the
                Member&rsquo;s property until a valid withdrawal request has been submitted and
                Check 2 of the Double Check Payment Framework has been satisfied.
              </li>
              <li>
                &ldquo;Contingent Right to Payment&rdquo; means the conditional and strictly
                personal right of a Member to receive a payment from Buygoon, which arises only
                upon satisfaction of both checks of the Double Check Payment Framework described
                in Section 9, and which does not constitute ownership of any funds at any time
                prior to that satisfaction.
              </li>
              <li>
                &ldquo;Cookie Wall&rdquo; means the full cookie wall operating at www.buygoon.com,
                which checks all access to the Platform behind the Member&rsquo;s explicit
                affirmative consent to all cookies and tracking technologies as a single integrated
                package.
              </li>
              <li>
                &ldquo;Member&rdquo; means a single individual human being who has registered for a
                Buygoon account in their personal capacity, accepted this Agreement, and satisfied
                all eligibility requirements in Section 4.
              </li>
              <li>
                &ldquo;Malicious Code&rdquo; means any viruses, Trojan horses, trap doors, back
                doors, Easter eggs, worms, time bombs, cancelbots, or other computer programming
                routines that are intended to damage, detrimentally interfere with, surreptitiously
                intercept, or expropriate the contents of any database or the normal operation of
                any computer system, or any harmful or malicious code, file, script, agent, or
                program designed to permit unauthorized access to, or to erase or otherwise harm,
                a party&rsquo;s software, hardware, systems, or data.
              </li>
              <li>
                &ldquo;Merchant Retailer&rdquo; means any participating retailer, brand, or merchant
                whose products or services are accessible through the Platform via affiliate
                tracking links.
              </li>
              <li>
                &ldquo;PayPal&rdquo; means PayPal Holdings, Inc. and its affiliated entities.
                PayPal is the sole payment mechanism through which Buygoon makes payments to
                Members. Buygoon is not affiliated with PayPal and is not responsible for any
                aspect of PayPal&rsquo;s services, terms, or policies.
              </li>
              <li>
                &ldquo;Pending Balance&rdquo; means an amount displayed in a Member&rsquo;s account
                that has not yet been verified as a final, irreversible Affiliate Commission
                deposit into Buygoon&rsquo;s account. A Pending Balance is informational only. It
                is not a promise, guarantee, representation, or commitment of any kind. It creates
                no legal obligation on Buygoon&rsquo;s part whatsoever. Buygoon makes no warranty
                that any Pending Balance will ever become a Confirmed Balance.
              </li>
              <li>
                &ldquo;Platform&rdquo; means the Buygoon cashback rewards and referral platform
                operating at www.buygoon.com, including all features, content, tools, and services
                made available thereon.
              </li>
              <li>
                &ldquo;Referral Network&rdquo; means the network of referral relationships recorded
                in Buygoon&rsquo;s systems in connection with the Buygoon Bonus Cash program,
                across up to four levels of referral connection. The Referral Network is the sole
                and exclusive property of Buygoon Inc. at all times.
              </li>
              <li>
                &ldquo;Double Check Payment Framework&rdquo; has the meaning given to it in Section
                9.
              </li>
            </ul>

            <h2>3. About Buygoon &mdash; How the Platform Works</h2>

            <h4>3.1 Nature of the Platform</h4>

            <p>
              Buygoon is a performance marketing intermediary and cashback rewards platform.
              Buygoon earns Affiliate Commissions from Merchant Retailers when Members shop through
              the Platform and shares a portion of those commissions with Members as Cashback.
              Buygoon does not sell goods or services. Buygoon is not a retailer. Buygoon is not a
              party to any transaction between a Member and any Merchant Retailer.
            </p>

            <h4>3.2 How Cashback Works</h4>

            <p>
              When a Member clicks through to a Merchant Retailer via a Buygoon affiliate link, two
              complementary attribution mechanisms activate simultaneously at the moment of
              click-through. First, Buygoon&rsquo;s system automatically appends a sub-ID &mdash;
              the Member&rsquo;s unique Buygoon numerical identifier &mdash; to the affiliate link.
              This sub-ID travels with the Member to the Merchant Retailer&rsquo;s website,
              identifying that Member individually to the Merchant Retailer and the applicable
              Affiliate Network Partner. Second, Buygoon places a tracking cookie on the
              Member&rsquo;s browser at the moment of click-through, confirming that the shopping
              session originated from Buygoon. When the Member completes a qualifying purchase, the
              Merchant Retailer reports the transaction to the applicable Affiliate Network Partner
              &mdash; tagged with both Buygoon&rsquo;s publisher identifier and the Member&rsquo;s
              unique sub-ID. The Affiliate Network Partner then reports that transaction back to
              Buygoon along with the applicable Affiliate Commission amount, and Buygoon&rsquo;s
              system uses the sub-ID to identify and credit the correct Member&rsquo;s account as
              Cashback, subject to the Double Check Payment Framework in Section 9.
            </p>

            <p>
              Both attribution mechanisms &mdash; the sub-ID appended to the affiliate link and the
              tracking cookie placed on the Member&rsquo;s browser &mdash; are non-optional.
              Together they are the fundamental technical mechanisms by which the Cashback service
              operates. Without both mechanisms functioning correctly and without interruption
              throughout the shopping session, Cashback cannot be accurately attributed, verified,
              or credited. By accepting the Cookie Wall and using the Platform, a Member consents
              to both mechanisms as necessary and non-severable components of the service.
            </p>

            <h4>3.3 Buygoon Bonus Cash</h4>

            <p>
              In addition to direct Cashback, Buygoon operates a four-level referral rewards
              program. When a Member refers new Members to the Platform and those Members shop
              through the Platform, the referring Member earns Buygoon Bonus Cash based on their
              downstream Referral Network&rsquo;s shopping activity across up to four levels of
              connection. The rates at which Buygoon Bonus Cash is earned at each level are
              published on the Platform and incorporated herein by reference. Those rates are
              subject to change at Buygoon&rsquo;s sole and absolute discretion at any time without
              advance notice. No Member has any vested contractual right to any particular Buygoon
              Bonus Cash rate at any level.
            </p>

            <h4>3.4 PayPal &mdash; Sole Payment Mechanism</h4>

            <p>
              PayPal is the sole mechanism by which Buygoon makes payments to Members. To receive
              Cashback or Buygoon Bonus Cash payments, Members must connect a valid PayPal account
              to their Buygoon account through PayPal&rsquo;s secure OAuth authentication flow.
              Buygoon does not receive, store, access, or hold any Member&rsquo;s PayPal
              credentials, bank account details, card numbers, or any other financial account
              information. Buygoon holds only a secure OAuth authorization token confirming that
              the PayPal connection has been established. All financial data remains with PayPal
              and is governed by PayPal&rsquo;s own privacy policy.
            </p>

            <h2>4. Eligibility</h2>

            <p>
              YOU MUST BE AT LEAST 18 YEARS OF AGE TO REGISTER FOR OR USE THE BUYGOON PLATFORM. IF
              YOU ARE UNDER 18 YEARS OF AGE, YOU ARE STRICTLY PROHIBITED FROM ACCESSING OR USING
              THIS PLATFORM. REGISTRATION BY ANY PERSON UNDER THE AGE OF 18 CONSTITUTES AN
              IMMEDIATE AND FUNDAMENTAL BREACH OF THIS AGREEMENT AND WILL RESULT IN IMMEDIATE
              PERMANENT ACCOUNT TERMINATION, A POTENTIAL PERMANENT BAN FROM THE PLATFORM, AND FULL
              FORFEITURE OF ALL ACCUMULATED BALANCES WITHOUT EXCEPTION, WITHOUT NOTICE, AND WITHOUT
              ANY RIGHT OF APPEAL OR REVIEW.
            </p>

            <h4>4.1 Age Requirement</h4>

            <p>
              By registering for a Buygoon account, you represent, warrant, and confirm on a
              continuing basis throughout your membership that you are at least 18 years of age.
              This representation is a fundamental, material, and non-severable term of this
              Agreement. Buygoon relies on this representation entirely and undertakes no
              independent age verification. PayPal independently restricts account opening to
              individuals 18 years of age and older.
            </p>

            <h4>4.2 Geographic Eligibility</h4>

            <p>
              The Platform is available exclusively to residents of the United States of America
              (all 50 states and Washington D.C.) and Canada, excluding the Province of Quebec. The
              Platform is not available to residents of the Province of Quebec, the European Union,
              the European Economic Area, the United Kingdom, Switzerland, or any other
              jurisdiction not expressly permitted. Buygoon reserves the right to modify the
              jurisdictions it serves at any time at its sole and absolute discretion, without
              notice and without liability.
            </p>

            <p>
              By registering, you represent and warrant on a continuing basis that you are located
              in and are a resident of a permitted jurisdiction. False representation of your
              jurisdiction of residence constitutes an immediate and fundamental breach of this
              Agreement and will result in immediate account termination, full forfeiture of all
              accumulated Cashback and Buygoon Bonus Cash balances, and reversal or withholding of
              any pending payments.
            </p>

            <h4>4.3 Personal Capacity &mdash; One Account Per Member</h4>

            <p>
              Every Buygoon membership account must be created and maintained by a single
              individual human being acting exclusively in their own personal capacity. Accounts
              may not be created, held, operated, or shared by or on behalf of any corporation,
              partnership, trust, association, or other legal entity of any kind. Accounts may not
              be shared between spouses, partners, family members, friends, colleagues, or any
              other individuals under any circumstances whatsoever.
            </p>

            <p>
              Each account represents a strictly personal, individual, non-transferable contractual
              relationship between one single natural person and Buygoon Inc. exclusively. Each
              person is limited to one account. Creation or operation of multiple accounts, or any
              account in a corporate capacity, on behalf of any legal entity, or shared with any
              other person, constitutes an immediate and fundamental breach of this Agreement
              subject to the full consequences in Sections 11 and 16, except where the prior
              written agreement of Buygoon has been obtained in each such instance, such agreement
              being granted or withheld at Buygoon&rsquo;s sole and unfettered discretion with no
              obligation on Buygoon to grant it under any circumstances whatsoever.
            </p>

            <h4>4.4 Minor Children in the Referral Network</h4>

            <p>
              No Member bears any responsibility, liability, or consequence for the unintentional
              presence of a minor at any position in their downstream Referral Network. A minor who
              misrepresents their age to register on the Platform is in fundamental breach of this
              Agreement. Upon discovery, Buygoon will immediately terminate that minor&rsquo;s
              account with full forfeiture of all accumulated balances and may impose a permanent
              ban. The minor&rsquo;s terminated position in the Referral Network becomes a Buygoon
              pass-through consistent with Section 7.5. No other Member&rsquo;s account, network
              position, or accumulated balances are affected.
            </p>

            <h2>5. Account Registration and Security</h2>

            <h4>5.1 Registration</h4>

            <p>
              To become a Member, you must register for an account by providing your email address,
              choosing a display name or pseudonym (real names are not required at any point), and
              accepting this Agreement and Buygoon&rsquo;s Privacy Policy through the Cookie Wall
              consent mechanism. By completing registration, you represent and warrant that all
              information you have provided is true, accurate, current, and complete. Upon
              registration, Members receive a unique referral code that they may share through any
              channel of their choosing; Members are solely responsible for ensuring that all
              referral code distribution complies with applicable anti-spam laws and all other
              referral link distribution requirements set out in Section 11.5.
            </p>

            <h4>5.2 Email Currency Obligation</h4>

            <p>
              Members are required to maintain a current and accurate email address in their
              Buygoon account at all times and must notify Buygoon immediately upon any change by
              updating their account information through the Platform. Notification sent by Buygoon
              to the email address on file constitutes valid and effective notice to the Member
              regardless of whether it is actually received. Buygoon bears no liability whatsoever
              for missed notices, payment delays, account actions, or any other consequence
              resulting from an inaccurate or outdated email address on file. This obligation is
              cross-referenced in Buygoon&rsquo;s Privacy Policy (Section 16) and is a material
              term of this Agreement.
            </p>

            <h4>5.3 Account Security</h4>

            <p>
              You are solely responsible for maintaining the confidentiality of your account
              credentials, including your password. You are solely responsible for all activity
              that occurs under your account. You must notify Buygoon immediately at{' '}
              <a href="mailto:legal@buygoon.com">legal@buygoon.com</a> if you become aware of any
              unauthorized use of your account or any security breach. Any use of your credentials
              will be deemed to be your use, and Buygoon is entitled to act on all instructions
              received under your credentials without liability.
            </p>

            <h4>5.4 Human Operation Requirement</h4>

            <p>
              Every instance of account creation, registration, access, operation, and Platform use
              must be performed by a single live human being acting individually, personally, and
              in real time. No form of automated, AI-assisted, bot-driven, or non-human operation
              of any account or any Platform function is permitted at any time for any purpose.
              This requirement is a fundamental, material, and non-severable term of this
              Agreement. Violation constitutes a fundamental breach subject to the full
              consequences in Sections 11 and 16.
            </p>

            <h4>5.5 Buygoon&rsquo;s Right to Suspend or Terminate</h4>

            <p>
              Buygoon reserves the right to suspend, restrict, or terminate any account at any
              time, at its sole and absolute discretion, for any reason or no reason, without
              notice and without liability. Buygoon&rsquo;s decision is final and binding and no
              process, hearing, or review is required before Buygoon acts. Where Buygoon exercises
              its right of suspension or termination for reasons other than a Member&rsquo;s breach
              of this Agreement, Confirmed Balances already standing in the Member&rsquo;s account
              at the time of such suspension or termination are preserved and remain accessible to
              the Member upon restoration of account access or upon submission of a valid
              withdrawal request, as applicable. For the avoidance of doubt, this preservation of
              Confirmed Balances does not apply where suspension or termination is a consequence
              of Member breach of this Agreement, in which case the forfeiture provisions of
              Section 11.7 apply in full without qualification or limitation.
            </p>

            <h2>6. Cookie Wall and Technology Consent</h2>

            <h4>6.1 All-or-Nothing Consent Model</h4>

            <p>
              Access to the Buygoon Platform is conditioned on the Member&rsquo;s explicit
              affirmative consent to all cookies and tracking technologies operated by Buygoon and
              its service providers, provided through the Cookie Wall. Consent is binary and
              all-inclusive. There is no selective opt-out of any kind. Members either consent to
              all cookies and tracking technologies as a single integrated package or they do not
              access the Platform. Declining consent is not an exercise of any privacy right
              &mdash; it is a failure to satisfy the minimum technical condition of service access.
              Buygoon&rsquo;s denial of access to Members who decline cookie consent is not
              retaliation, discrimination, or a violation of any right under any applicable law.
            </p>

            <p>
              The tracking technologies that enable Cashback attribution, Referral Network
              tracking, analytics, platform personalization, and geographic access control are
              collectively fundamental to the Platform and cannot be selectively disabled without
              disabling the service entirely.
            </p>

            <h4>6.2 Consent Records</h4>

            <p>
              A Member&rsquo;s affirmative acceptance of the Cookie Wall is recorded, timestamped,
              and stored by Cookiebot (Usercentrics A/S). This acceptance constitutes explicit
              prior informed consent that governs over any browser-level or device-level privacy
              preference signal, including signals sent via the Global Privacy Control or any
              similar mechanism.
            </p>

            <h4>6.3 Withdrawal of Consent</h4>

            <p>
              A Member may withdraw cookie consent at any time via the Privacy Settings icon on the
              Platform. Withdrawal takes immediate effect and immediately terminates the
              Member&rsquo;s active Platform access and ability to earn future Cashback or Buygoon
              Bonus Cash. Confirmed Balances already standing in the Member&rsquo;s account are
              preserved and remain accessible upon re-consent and login, subject to all conditions
              of this Agreement. Withdrawal of cookie consent does not constitute account
              cancellation under Section 16.
            </p>

            <h4>6.4 Affiliate Attribution Disclosures</h4>

            <p>
              As a mandatory and non-severable condition of the Cashback service, Buygoon discloses
              Member browsing and purchase attribution data to Affiliate Network Partners including
              Commission Junction, Impact.com, and any others through whom Cashback attribution is
              facilitated. These Affiliate Network Partners may use attribution data for their own
              purposes. This data use is governed by their own privacy policies. By accepting the
              Cookie Wall, Members consent to these disclosures as a non-severable component of
              the Cashback service. There is no mechanism to opt out of affiliate attribution
              disclosures while continuing to use the Platform.
            </p>

            <h2>7. Buygoon Bonus Cash &mdash; Referral Rewards Program</h2>

            <h4>7.1 Program Overview</h4>

            <p>
              Buygoon operates a four-level referral rewards program under the exclusive name
              Buygoon Bonus Cash. When a Member refers new Members to the Platform and those new
              Members shop through the Platform, the referring Member earns Buygoon Bonus Cash
              based on the downstream Referral Network&rsquo;s shopping activity, across up to
              four levels of referral connection. Buygoon Bonus Cash is subject to the Double
              Check Payment Framework in Section 9 and all other applicable terms of this
              Agreement.
            </p>

            <h4>7.2 Earning Buygoon Bonus Cash</h4>

            <p>
              A Member earns Buygoon Bonus Cash when Members in their downstream Referral Network
              complete qualifying purchases through the Platform and the corresponding Affiliate
              Commissions are received by Buygoon as final, irreversible deposits. The applicable
              earning rates at each level are published on the Platform and incorporated herein by
              reference. Rates may differ between Members and may be modified by Buygoon at any
              time in its sole and absolute discretion without advance notice. No Member has any
              vested contractual right to any particular rate at any level.
            </p>

            <p>
              Buygoon&rsquo;s interests are fully aligned with those of its Members in ensuring
              that Affiliate Commissions are collected. Buygoon does not earn its own revenue
              unless the Affiliate Commission is received from the Merchant Retailer &mdash; the
              same commission from which Member Cashback and Buygoon Bonus Cash are paid. Members
              and Buygoon share a common interest in pursuing unconfirmed transactions.
            </p>

            <p>
              By way of illustration only, the standard Buygoon Bonus Cash calculation operates as
              follows. For each completed qualifying purchase made by a member in your downstream
              Referral Network, you earn Buygoon Bonus Cash equal to 10% of the Cashback that
              member earned on that purchase, applied uniformly across all four levels of your
              network. For example: if a member in your first ripple earns $20.00 in Cashback on a
              qualifying purchase, you earn $2.00 in Buygoon Bonus Cash from that transaction; if a
              member in your third ripple earns $50.00 in Cashback on a qualifying purchase, you
              earn $5.00 in Buygoon Bonus Cash from that transaction. The same 10% calculation
              applies equally to members in your second and fourth ripples. These examples are
              illustrative only and are provided solely for ease of understanding. The applicable
              Buygoon Bonus Cash rate at each level is always as published on the Platform or as
              otherwise notified to the individual Member from time to time, is subject to change
              at Buygoon&rsquo;s sole and absolute discretion at any time without advance notice,
              and may differ for specific Members in special circumstances as determined by Buygoon
              in its sole discretion.
            </p>

            <h4>7.3 Passive Earning</h4>

            <p>
              Members may earn Buygoon Bonus Cash passively from the shopping activity of their
              downstream Referral Network regardless of their own personal shopping activity on the
              Platform, provided their account remains active. The inactive account framework in
              Section 15 applies to all accounts.
            </p>

            <h4>7.4 Referral Network Visibility and Privacy</h4>

            <p>
              Your display name is visible to the Member who directly referred you, and you can
              see the display names of Members you have directly referred. No other personal
              information is shared between Members at any level of the Referral Network, including
              without limitation email addresses, Cashback amounts, purchase history, transaction
              details, or Merchant Retailer activity. Members may use any display name or
              pseudonym. Real names are never required.
            </p>

            <h4>7.5 Network Integrity &mdash; Vacancies and Pass-Throughs</h4>

            <p>
              The integrity of the four-level Referral Network is preserved regardless of what
              happens to any individual Member within it. When any Member&rsquo;s position becomes
              vacant &mdash; whether by reason of termination, voluntary account cancellation,
              permanent closure due to inactivity, death, or any other reason whatsoever &mdash;
              that position does not break the chain. The vacant position automatically becomes a
              silent pass-through to Buygoon. Any Buygoon Bonus Cash that would otherwise have been
              credited to the vacant position is credited to Buygoon directly. All Members above
              and below the vacant position are entirely unaffected. Their network relationships,
              earning positions, and Buygoon Bonus Cash credits from their respective downstream
              activity continue exactly as before.
            </p>

            <h4>7.6 Ownership of the Referral Network</h4>

            <p>
              The Referral Network &mdash; including every referral relationship, every level of
              connection, every downstream Member association, and the network structure itself
              &mdash; is the sole and exclusive property of Buygoon Inc. at all times. No Member
              acquires any ownership interest, intellectual property right, contractual
              entitlement, vested right, or proprietary claim of any kind in any referral
              relationship, network position, or Referral Network structure by virtue of referring
              other Members, building a downstream network, or participating in the Buygoon Bonus
              Cash program for any period of time. The Referral Network exists on Buygoon&rsquo;s
              Platform, is recorded in Buygoon&rsquo;s systems, and belongs to Buygoon entirely.
              Members are granted a revocable privilege to participate in the Referral Network
              subject to this Agreement, and nothing more.
            </p>

            <h4>7.7 Program Modification and Termination</h4>

            <p>
              Buygoon reserves the absolute right to modify, suspend, or terminate the Buygoon
              Bonus Cash program &mdash; including its structure, the number of levels, the
              earning rates at any or all levels, the eligibility criteria, or any other feature
              &mdash; immediately upon notice, at its sole and absolute discretion. No advance
              notice is required. Notice will be provided to Members via email to the address on
              file, effective immediately upon sending. No Member has any vested contractual right
              in the continuation of the Buygoon Bonus Cash program at any particular structure,
              level count, or rate. No program modification or termination results in the
              retroactive clawback of any Confirmed Balance.
            </p>

            <h2>8. How the Cashback Service Works</h2>

            <h4>8.1 Earning Cashback</h4>

            <p>
              To earn Cashback, you must: (a) be registered and signed in to your Buygoon account;
              (b) click through to a Merchant Retailer using a Buygoon affiliate link on the
              Platform, at which moment Buygoon&rsquo;s system automatically appends your unique
              Buygoon numerical identifier as a sub-ID to the affiliate link and places a tracking
              cookie on your browser; (c) complete your purchase during the same shopping session
              initiated by that click-through; and (d) have both the sub-ID and the tracking cookie
              remain active, intact, and uninterrupted throughout the shopping session. If you
              visit other websites before completing your purchase, use coupons or discount codes
              not provided by Buygoon, disable cookies on your browser, or otherwise interfere
              with either attribution mechanism, your purchase may not be attributed to Buygoon
              and you may not earn Cashback.
            </p>

            <h4>8.2 Cashback Exclusions</h4>

            <p>
              Cashback is earned on the net purchase amount, which excludes taxes, fees, shipping
              charges, gift-wrapping, discounts or credits, returns or cancellations, and extended
              warranties. Cashback amounts vary by Merchant Retailer and product category and may
              contain additional exclusions in the terms of the applicable offer. Members are
              responsible for reviewing applicable offer terms before initiating any shopping
              session.
            </p>

            <h4>8.3 Pending Transactions and the 90-Day Inquiry Window</h4>

            <p>
              After completing a qualifying purchase, a Pending Balance may appear in your account.
              A Pending Balance is informational only &mdash; it is not a guarantee, promise, or
              commitment of any kind, and creates no legal obligation on Buygoon&rsquo;s part. If a
              transaction remains as a Pending Balance and has not converted to a Confirmed
              Balance, Members may submit a written inquiry to Buygoon within 90 days of the date
              of the transaction. Inquiries submitted after 90 days will not be considered. To
              submit an inquiry, Members must provide all of the following:
            </p>

            <ul>
              <li>The name of the Merchant Retailer</li>
              <li>The date and time of the Buygoon click-through that initiated the shopping session</li>
              <li>The date and time of the purchase</li>
              <li>A description of the products or services purchased</li>
              <li>The exact purchase amount</li>
              <li>The order confirmation number issued by the Merchant Retailer</li>
              <li>A copy or screenshot of the order confirmation from the Merchant Retailer</li>
              <li>
                Whether any coupons, discount codes, or promotional codes not provided by Buygoon
                were used
              </li>
              <li>
                Whether any other websites were visited between the Buygoon click-through and the
                completion of the purchase
              </li>
              <li>The browser and device used to complete the shopping session</li>
            </ul>

            <p>
              Submission of a pending transaction inquiry creates no obligation on Buygoon to
              investigate, escalate, confirm, credit, or pay any amount. Buygoon retains sole and
              absolute discretion over whether and how to respond. Buygoon is motivated to pursue
              unconfirmed transactions &mdash; Buygoon&rsquo;s own revenue depends on the same
              Affiliate Commissions from which Member Cashback and Buygoon Bonus Cash are paid
              &mdash; and will make such efforts as Buygoon deems reasonable to investigate valid
              inquiries submitted with complete information, the determination of what constitutes
              reasonable efforts resting exclusively with Buygoon.
            </p>

            <h2>9. Payment Terms</h2>

            <p>
              THE FOLLOWING TWO-CHECK PAYMENT FRAMEWORK IS A FUNDAMENTAL AND MATERIAL TERM OF THIS
              AGREEMENT WITHOUT WHICH BUYGOON WOULD NOT HAVE PROVIDED ACCESS TO THE PLATFORM.
              PLEASE READ THIS SECTION IN ITS ENTIRETY.
            </p>

            <h4>9.1 The Double Check Payment Framework</h4>

            <p>
              NO PAYMENT OBLIGATION OF ANY KIND EXISTS UNTIL BOTH OF THE FOLLOWING CHECKS HAVE BEEN
              SATISFIED IN SEQUENCE:
            </p>

            <p>
              <b>CHECK 1 &mdash; FINAL IRREVERSIBLE AFFILIATE COMMISSION DEPOSIT:</b> The
              corresponding Affiliate Commission must be received by Buygoon as a final,
              irreversible deposit from the applicable Affiliate Network Partner into
              Buygoon&rsquo;s own account. Until that moment, no obligation exists, no entitlement
              exists, and any Pending Balance displayed in a Member&rsquo;s account is
              informational only. A Pending Balance is not a promise, guarantee, representation, or
              commitment of any kind. The display of a Pending Balance creates no legal obligation
              on Buygoon whatsoever. Buygoon makes no warranty that any Pending Balance will ever
              become a Confirmed Balance.
            </p>

            <p>
              <b>CHECK 2 &mdash; VALID WITHDRAWAL REQUEST:</b> Even once Check 1 is satisfied and an
              Affiliate Commission has been irreversibly deposited and credited to a
              Member&rsquo;s account as a Confirmed Balance, that Confirmed Balance does NOT become
              the property of the Member. It remains a Contingent Right to Payment &mdash; personal
              to the Member, non-transferable, and not the Member&rsquo;s property. The
              Member&rsquo;s Contingent Right to Payment crystallizes into an actual payment
              obligation on Buygoon&rsquo;s part ONLY upon the Member submitting a valid withdrawal
              request through Buygoon&rsquo;s payment system, subject to the minimum payment
              threshold of USD $5.01.
            </p>

            <h4>9.2 Currency and Payment Mechanism</h4>

            <p>
              All Cashback and Buygoon Bonus Cash balances are denominated and paid exclusively in
              United States dollars (USD). Payment is made exclusively via PayPal. Members must
              maintain a valid, active PayPal account connected to their Buygoon account as a
              condition of receiving payment. A single PayPal email address cannot be connected to
              multiple Buygoon accounts.
            </p>

            <p>
              Buygoon bears zero responsibility for currency conversion of any kind. Canadian
              Members receiving USD into their PayPal accounts are solely responsible for ensuring
              their PayPal account can receive USD and for any conversion, exchange rate
              implications, or fees that may result. Any fees charged by PayPal in connection with
              receiving, holding, or converting USD payments are solely the Member&rsquo;s
              responsibility.
            </p>

            <h4>9.3 Minimum Payment Threshold</h4>

            <p>
              The minimum withdrawal amount is USD $5.01. Withdrawal requests below USD $5.01 will
              not be processed and balances below the minimum threshold roll over.
            </p>

            <h4>9.4 Discharge of Payment Obligation</h4>

            <p>
              Buygoon&rsquo;s payment obligation is fully and completely discharged upon Buygoon
              initiating a valid PayPal transfer to the Member&rsquo;s connected PayPal account.
              What occurs after that moment is entirely between the Member and PayPal. Buygoon
              bears zero responsibility for any failure, delay, error, refusal, currency
              configuration issue, fee, limitation, suspension, account closure, or any other
              issue arising within PayPal&rsquo;s systems or between PayPal and the Member after
              transfer initiation. The Member&rsquo;s sole recourse for any post-transfer issue is
              directly against PayPal.
            </p>

            <h4>9.5 Clawback Rights</h4>

            <p>
              Buygoon&rsquo;s design intention is that only final, irreversible commission deposits
              are displayed as Confirmed Balances. However, if an Affiliate Commission is reversed
              by an Affiliate Network Partner after being deposited and credited &mdash; due to a
              product return, order cancellation, chargeback, fraud determination, or any other
              reason &mdash; Buygoon retains the unconditional right to claw back the corresponding
              amount from the affected Member&rsquo;s account without notice. A clawback may drive
              the Member&rsquo;s balance into negative territory. There is no floor at zero for
              clawbacks. A negative balance resulting from a clawback operates as an automatic
              offset against all future Cashback and Buygoon Bonus Cash credits to that
              Member&rsquo;s account. The Member&rsquo;s right to submit a withdrawal request is
              suspended during any period in which their balance is negative or zero as a result of
              a clawback. This offset right survives account reactivation and continues until the
              negative balance is fully extinguished by future credits.
            </p>

            <h4>9.6 Non-Transferability and Creditor Protection</h4>

            <p>
              The Contingent Right to Payment, including any Pending Balance or Confirmed Balance,
              is strictly personal to the Member and non-transferable. To the fullest extent
              permitted by applicable law:
            </p>

            <ul>
              <li>
                No Member may pledge, assign, encumber, hypothecate, or use as collateral any
                balance standing in their Buygoon account. Any purported assignment, pledge, or
                encumbrance is void ab initio.
              </li>
              <li>
                No creditor of any Member &mdash; whether judgment creditor, secured creditor,
                unsecured creditor, trustee in bankruptcy, receiver, garnishing creditor, or any
                other person claiming through or against a Member &mdash; has any right, title,
                interest, claim, or remedy of any kind against any balance standing in a
                Member&rsquo;s Buygoon account. To the fullest extent permitted by applicable law,
                Member account balances are not attachable, garnishable, seizable, or available to
                satisfy any debt, judgment, or claim against any Member.
              </li>
              <li>
                Buygoon will not recognize, honour, or respond to any garnishment, attachment,
                execution, assignment order, or other legal process purporting to claim, freeze,
                transfer, or encumber any Member account balance, except where compelled to do so
                by a final and binding court order from a court of competent jurisdiction in
                Ontario, Canada, following which Buygoon reserves all rights to contest such order.
              </li>
              <li>
                Notwithstanding the foregoing, Buygoon reserves the right, entirely at its sole and
                absolute discretion, to make a discretionary payment to a Member&rsquo;s creditor
                upon receipt of such documentation as Buygoon may require. Any such discretionary
                payment creates no obligation to make similar payments in any other case. The
                affected Member expressly waives any and all claims and recourse against Buygoon
                arising out of any such discretionary payment.
              </li>
            </ul>

            <h4>9.7 Death of a Member</h4>

            <p>
              Buygoon membership accounts are strictly personal and non-transferable. To the
              fullest extent permitted by applicable law, a Buygoon account and any balance within
              it cannot be transferred, assigned, bequeathed, or conveyed to any estate, heir,
              beneficiary, executor, administrator, trustee, or any other third party by operation
              of law or otherwise.
            </p>

            <p>
              Any unwithdrawn balance in a deceased Member&rsquo;s account was never the property
              of the deceased Member at the time of their death. Since the Contingent Right to
              Payment crystallizes only upon submission of a valid withdrawal request by the Member
              personally, and since a deceased Member is incapable of submitting such a request, no
              payment obligation ever crystallized in respect of any unwithdrawn balance prior to
              death. Accordingly, there is nothing to form part of the deceased Member&rsquo;s
              estate.
            </p>

            <p>
              Notwithstanding the foregoing, Buygoon reserves the right, entirely at its sole and
              absolute discretion, to make a discretionary payment to a verified estate upon
              receipt of such documentation as Buygoon may require, including without limitation a
              death certificate and letters probate or letters of administration. Exercise of this
              discretion in any particular case creates no obligation to exercise it in any other
              case.
            </p>

            <h4>9.8 Corporate Transactions &mdash; Unclaimed Balances</h4>

            <p>
              The existence of unclaimed or unwithdrawn Member account balances creates no
              restriction, encumbrance, obligation, or liability of any kind on Buygoon&rsquo;s
              ability to enter into any corporate transaction whatsoever, including without
              limitation any sale of the company in whole or in part, sale of assets, merger,
              acquisition, change of control, reorganization, financing, initial public offering,
              dissolution, or any other corporate transaction. Since unwithdrawn balances are not
              the property of Members, they do not constitute a liability that attaches to,
              encumbers, or restricts any corporate transaction. Members expressly acknowledge and
              agree that Buygoon&rsquo;s ability to enter into any corporate transaction is
              entirely unrestricted by the existence of any unwithdrawn balance in any Member
              account. Any acquirer, successor, or transferee steps into Buygoon&rsquo;s position
              with respect to those balances on exactly the same terms as this Agreement.
            </p>

            <h2>10. Tax Reporting and Member Tax Obligations</h2>

            <h4>10.1 Overview</h4>

            <p>
              Buygoon is required by law to collect, retain, and report financial information about
              Members who receive Cashback and Buygoon Bonus Cash payments above applicable
              reporting thresholds under United States and Canadian tax law.
            </p>

            <h4>10.2 United States Members &mdash; IRS Form 1099</h4>

            <p>
              Buygoon is required to file IRS Form 1099 for any Member who receives USD $600 or
              more in combined Cashback and Buygoon Bonus Cash payments in a single calendar year.
              The tax identification collection obligation is triggered exclusively by a Member
              submitting a valid withdrawal request. At that point, if the requested withdrawal
              amount combined with any prior payments to that Member in the same calendar year
              meets or exceeds USD $600, Buygoon will require the following as a condition
              precedent to processing the withdrawal:
            </p>

            <ul>
              <li>Legal name</li>
              <li>Mailing address</li>
              <li>Social Security Number (SSN) or Individual Taxpayer Identification Number (ITIN)</li>
            </ul>

            <p>
              Payment is suspended pending the provision of all required information to
              Buygoon&rsquo;s satisfaction. Refusal to provide required tax identification
              information will result in Buygoon applying mandatory backup withholding to the
              Member&rsquo;s payments in accordance with applicable IRS requirements.
            </p>

            <h4>10.3 Canadian Members &mdash; CRA Reporting</h4>

            <p>
              Buygoon is required to comply with Canada Revenue Agency (CRA) reporting requirements
              for payments to individuals. Where CRA reporting obligations apply, Buygoon will
              require the following as a condition precedent to processing a withdrawal:
            </p>

            <ul>
              <li>Legal name</li>
              <li>Mailing address</li>
              <li>Social Insurance Number (SIN) or Business Number (BN), where applicable</li>
            </ul>

            <p>
              Payment is suspended pending the provision of all required information. Applicable
              reporting thresholds under CRA requirements apply and are subject to change.
            </p>

            <h4>10.4 Member Tax Responsibilities</h4>

            <p>
              By using Buygoon and receiving Cashback or Buygoon Bonus Cash payments, each Member
              acknowledges and agrees that: (a) Buygoon is required by law to report payments
              above applicable thresholds to the IRS, CRA, and any other applicable tax authority;
              (b) the Member is solely responsible for accurately reporting all income received
              through Buygoon on their personal or business tax returns in accordance with the
              laws of their jurisdiction; and (c) refusal to provide required tax identification
              information may result in mandatory backup withholding in accordance with applicable
              law.
            </p>

            <h4>10.5 Record Retention</h4>

            <p>
              Buygoon retains transaction records, payment records, Cashback and Buygoon Bonus
              Cash attribution data, and any associated personal information &mdash; including
              legal names and tax identification numbers &mdash; for a minimum period of seven
              years following the end of the tax year in which the relevant transaction occurred,
              consistent with the requirements of the Income Tax Act (Canada), Canada Revenue
              Agency record-keeping requirements, and applicable United States Internal Revenue
              Service record-keeping requirements. Buygoon reserves the right, in its sole and
              absolute discretion, to retain any such records for any period beyond seven years as
              Buygoon determines appropriate. The seven-year period is a minimum floor, not a
              maximum limit. This retention obligation survives account cancellation or termination
              and cannot be overridden by any Member deletion request.
            </p>

            <h4>10.6 Tax Non-Compliance &mdash; Deemed Waiver of Payment Entitlement</h4>

            <p>
              A Member who fails or refuses to provide any tax identification information required
              by Buygoon to fulfill its reporting or filing obligations to the IRS, CRA, or any
              other applicable tax authority is deemed, for so long as such failure or refusal
              continues, to have waived their entitlement to receive any payment from Buygoon in
              respect of any amount standing in their account. This deemed waiver operates as an
              additional condition precedent to payment, consistent with and reinforcing the
              Double Check Payment Framework in Section 9, and applies to any Member who has
              reached an applicable reporting threshold under United States or Canadian tax law.
              The deemed waiver is not permanent &mdash; it is lifted automatically upon the
              Member providing compliant and complete tax identification information to
              Buygoon&rsquo;s satisfaction &mdash; but until that condition is satisfied, no
              payment obligation exists on Buygoon&rsquo;s part and Buygoon is under no obligation
              to hold any amount in the Member&rsquo;s account indefinitely pending compliance.
              For greater certainty, this provision does not extinguish any Confirmed Balance
              standing in the Member&rsquo;s account; it suspends the Member&rsquo;s entitlement to
              receive payment of that balance until tax compliance is achieved.
            </p>

            <h2>11. Prohibited Conduct</h2>

            <h4>11.1 General Prohibition</h4>

            <p>
              Members must not engage in any conduct that is inconsistent with the integrity of
              the Platform, the Referral Network, or the Cashback system, as determined by Buygoon
              in its sole and absolute discretion. The following is a non-exhaustive illustrative
              list of prohibited conduct. The absence of any specific behaviour from this list does
              not mean it is permitted.
            </p>

            <h4>11.2 Referral and Platform Abuse</h4>

            <p>
              The following conduct constitutes prohibited abuse, including without limitation:
            </p>

            <ul>
              <li>Self-referral &mdash; referring oneself using any means whatsoever</li>
              <li>
                Fictitious accounts &mdash; creating, operating, or participating in accounts using
                false, fabricated, or misappropriated identities
              </li>
              <li>
                Coordinated referral rings &mdash; organizing, participating in, or facilitating
                any arrangement among Members to artificially generate referral relationships or
                activity
              </li>
              <li>
                Automated account creation &mdash; using any automated tool, script, bot, expert
                system, or non-human means to create accounts
              </li>
              <li>
                Manipulation of affiliate tracking &mdash; using any means to simulate, spoof,
                manipulate, or falsify click-through events, shopping sessions, or purchase
                confirmations for the purpose of generating Cashback or Buygoon Bonus Cash
                attribution
              </li>
              <li>
                Artificial inflation &mdash; any conduct designed to artificially inflate Cashback
                or Buygoon Bonus Cash earnings at any level of the Referral Network
              </li>
            </ul>

            <h4>11.3 AI and Automated Access Prohibition</h4>

            <p>
              Every instance of account creation, registration, access, operation, and Platform use
              must be performed by a single live human being acting individually, personally, and
              in real time. The following is expressly prohibited:
            </p>

            <ul>
              <li>
                Using any artificial intelligence tool, automated script, bot, expert system, or
                any other non-human or AI-assisted means to create an account, access an account,
                or perform any membership operation &mdash; including without limitation clicking
                through to Merchant Retailers, initiating shopping sessions, submitting withdrawal
                requests, or interacting with any Platform feature
              </li>
              <li>
                Using AI-generated synthetic identities &mdash; including AI-generated names,
                addresses, profile photos, or other fabricated identity attributes &mdash; to
                register or operate an account
              </li>
              <li>
                Using any automated or AI-assisted tool to simulate, spoof, or manipulate
                click-through events, shopping sessions, or purchase confirmations for the purpose
                of generating Cashback or Buygoon Bonus Cash attribution
              </li>
              <li>
                Using any automated or AI-assisted means to generate, simulate, or coordinate
                referral relationships or referral network activity across any level of the
                Buygoon Bonus Cash structure
              </li>
              <li>
                Using any automated or AI-assisted tool to interact with Buygoon&rsquo;s member
                services, payment systems, or withdrawal request mechanisms
              </li>
            </ul>

            <h4>11.4 Additional Prohibited Conduct</h4>

            <p>The following additional conduct is also prohibited:</p>

            <ul>
              <li>Creating or operating more than one Buygoon account</li>
              <li>
                Creating or operating any account in a corporate capacity, on behalf of any legal
                entity, or shared with any other person
              </li>
              <li>Falsely representing your jurisdiction of residence</li>
              <li>
                Accessing or attempting to access the Platform from any jurisdiction in which the
                Platform is not available
              </li>
              <li>
                Damaging, interfering with, or unreasonably overloading the Platform or its
                infrastructure
              </li>
              <li>Introducing into the Platform any Malicious Code (as defined in Section 2)</li>
              <li>
                Modifying, reverse engineering, decompiling, copying, or otherwise attempting to
                derive the source code of any component of the Platform
              </li>
              <li>
                Using any software, device, or process to scrape, crawl, spider, or index any
                content from the Platform
              </li>
              <li>
                Attacking the Platform via denial-of-service attack, distributed denial-of-service
                attack, flooding, mailbombing, or crashing
              </li>
              <li>Engaging in any activity that violates applicable law or regulations</li>
            </ul>

            <h4>11.5 Referral Link Distribution Restrictions</h4>

            <p>
              Members who distribute Buygoon referral links through any channel &mdash; including
              social media, email, websites, blogs, videos, podcasts, or any other means &mdash;
              are subject to the following requirements, violation of any of which constitutes a
              fundamental breach of this Agreement:
            </p>

            <ul>
              <li>
                <b>PAID ADVERTISING PROHIBITION:</b> Members are expressly prohibited from running
                paid advertising campaigns on any platform &mdash; including without limitation
                Google, Meta, Instagram, TikTok, YouTube, X, or any search engine or social media
                platform &mdash; using Buygoon&rsquo;s name, trademark, logo, &ldquo;Buygoon Bonus
                Cash,&rdquo; or any confusingly similar term as a keyword, in ad copy, in display
                advertising, or in any other paid promotional format, without Buygoon&rsquo;s prior
                express written consent.
              </li>
              <li>
                <b>MISREPRESENTATION PROHIBITION:</b> Members are expressly prohibited from making
                any false, misleading, exaggerated, or unsubstantiated representations about the
                Platform, its Cashback rates, the Buygoon Bonus Cash program, or any earning
                potential. Members may not promise, guarantee, or imply specific earnings,
                guaranteed returns, or any outcome that Buygoon itself does not guarantee in this
                Agreement or on the Platform.
              </li>
              <li>
                <b>ANTI-SPAM COMPLIANCE:</b> Members are expressly prohibited from distributing
                Buygoon referral links through unsolicited bulk email, spam, or any other
                communication that violates any applicable anti-spam legislation, including
                without limitation Canada&rsquo;s Anti-Spam Legislation (CASL) and the United
                States CAN-SPAM Act. Members are solely responsible for ensuring their referral
                link distribution complies with all applicable anti-spam laws in their
                jurisdiction.
              </li>
              <li>
                <b>FTC AND REGULATORY DISCLOSURE COMPLIANCE:</b> Any Member who publicly promotes,
                discusses, recommends, or references Buygoon &mdash; through social media posts,
                blog posts, videos, podcasts, reviews, or any other public communication &mdash;
                in connection with their participation in the Platform or the Buygoon Bonus Cash
                program must clearly and conspicuously disclose their compensated relationship
                with Buygoon in accordance with all applicable laws and regulations governing
                endorsements and testimonials, including without limitation the United States
                Federal Trade Commission&rsquo;s Endorsement Guides as amended from time to time,
                and any equivalent Canadian federal or provincial regulations. Such disclosure
                must be clear, prominent, and unambiguous, must appear in close proximity to any
                reference to Buygoon, and must not be buried, obscured, or omitted. Buygoon
                expressly reserves the right to report non-compliant Members to the FTC or any
                applicable regulatory authority at its sole and absolute discretion.
              </li>
            </ul>

            <p>
              Members must not promise to anyone, in any form or through any channel, that they
              will save or earn any specific or general amount of money by using the Buygoon
              platform. Any such promise constitutes a fundamental breach of this Agreement and is
              subject to the full consequences set out in Section 11.7.
            </p>

            <h4>11.6 Conspiracy and Collusion</h4>

            <p>
              No Member bears any responsibility or liability for the conduct of any other Member
              at any position in their downstream Referral Network, except where Buygoon
              determines, in its sole and absolute discretion, that the upline Member engaged in,
              facilitated, encouraged, coordinated, or conspired in abusive, fraudulent, or
              prohibited conduct involving one or more downstream Members. In such circumstances,
              the upline Member is treated as a direct participant in the abuse and is subject to
              the full consequences in Section 11.7.
            </p>

            <h4>11.7 Consequences of Any Breach</h4>

            <p>
              Any breach of this Agreement &mdash; including without limitation any prohibited
              conduct described in this Section &mdash; entitles Buygoon, in its sole and absolute
              discretion, to:
            </p>

            <ul>
              <li>Immediately suspend or permanently terminate the offending Member&rsquo;s account</li>
              <li>
                Declare a full forfeiture of the entire accumulated balance &mdash; both Cashback
                and Buygoon Bonus Cash &mdash; regardless of whether any portion was earned
                legitimately prior to the breach
              </li>
              <li>
                Declare a full forfeiture of entire accumulated balances in any related, fictitious,
                or associated accounts that Buygoon can identify as connected to the offending
                Member
              </li>
              <li>Reverse or withhold any pending payments not yet issued</li>
              <li>
                Impose a permanent ban on the offending individual from registering any future
                account on the Platform
              </li>
            </ul>

            <p>
              Buygoon&rsquo;s determination is final, binding, and made in Buygoon&rsquo;s sole and
              absolute discretion. No process, hearing, notice, or review of any kind is required
              before Buygoon acts. Buygoon is not obligated to provide reasons.
            </p>

            <p>
              A Member who believes Buygoon has acted in error may submit a written complaint to{' '}
              <a href="mailto:legal@buygoon.com">legal@buygoon.com</a>. Buygoon is under no
              obligation whatsoever to acknowledge, consider, respond to, or act upon any such
              complaint. Submission does not stay, suspend, or reverse any action taken. No
              procedural right, appeal right, or due process entitlement is created by the
              submission of a complaint.
            </p>

            <h2>12. Merchant Relationships</h2>

            <h4>12.1 Buygoon Is Not the Merchant</h4>

            <p>
              The Member transacts directly and exclusively with each Merchant Retailer. Buygoon is
              not a party to any transaction between a Member and any Merchant Retailer. Buygoon is
              not the merchant, seller, or agent of any Merchant Retailer and is not responsible in
              any way for any aspect of any Member-Merchant Retailer transaction, including without
              limitation pricing, product descriptions, product availability, product quality,
              shipping, delivery, returns, exchanges, refunds, cancellations, warranties, or any
              Merchant Retailer policy of any kind.
            </p>

            <h4>12.2 Merchant Independence</h4>

            <p>
              Merchant Retailers operate entirely independently and are not under Buygoon&rsquo;s
              control. Buygoon is not the agent of any Merchant Retailer. Any dispute, issue, or
              claim arising from a transaction between a Member and a Merchant Retailer is solely
              between the Member and that Merchant Retailer. Buygoon assumes no liability,
              obligation, or responsibility for any part of any Member-Merchant Retailer
              interaction or correspondence.
            </p>

            <h4>12.3 Cashback Contingency</h4>

            <p>
              Cashback is always contingent on the applicable Merchant Retailer confirming the
              transaction through the applicable Affiliate Network Partner and Buygoon receiving
              the corresponding Affiliate Commission as a final, irreversible deposit. Buygoon
              bears no responsibility if a Merchant Retailer fails to report a transaction, fails
              to make payment for any reason, withdraws from the Platform, changes its Cashback
              rate, modifies or discontinues any offer, or ceases participation in any Affiliate
              Network &mdash; at any time, with or without notice, for any reason or no reason.
            </p>

            <h4>12.4 Platform Flexibility</h4>

            <p>
              Buygoon reserves the right to add, remove, modify, suspend, or discontinue any
              Merchant Retailer, offer, Cashback rate, product category, or any other feature of
              the Platform at any time at its sole and absolute discretion, without notice and
              without liability. Buygoon makes no representations or warranties regarding any
              Merchant Retailer, any product or service offered by any Merchant Retailer, the
              accuracy of any pricing information, or the continued availability of any Cashback
              rate or offer. For greater certainty, Buygoon has sole and unfettered discretion
              over: the selection of Merchant Retailers featured on the Platform; the placement,
              prominence, and positioning of any retailer link or advertisement on the Platform;
              the manner, format, and timing of any such placement or promotion; and the
              commercial terms on which any Merchant Retailer is featured &mdash; all without any
              obligation to any Member or any Merchant Retailer whatsoever. No Member has any
              right to demand or expect that any particular Merchant Retailer be featured on the
              Platform, or that any retailer link or advertisement be placed in any particular
              location or manner.
            </p>

            <h4>12.5 Compensation Disclosure</h4>

            <p>
              Compensation received by Buygoon from Merchant Retailers may play a part in whether
              Merchant Retailers and products appear on the Platform, where they are placed, and
              how they are promoted to Members.
            </p>

            <h2>13. Third-Party Platforms and Software</h2>

            <h4>13.1 Third-Party Software Generally</h4>

            <p>
              Buygoon may from time to time make available on or through the Platform access to
              third-party software, applications, tools, services, or platforms (collectively,
              &ldquo;Third-Party Software&rdquo;) to enhance or supplement the Member experience.
              Current examples include PayPal for payment processing. Future examples may include
              charitable giving platforms or any other third-party services that Buygoon elects to
              make accessible through the Platform from time to time.
            </p>

            <p>
              The availability of any Third-Party Software on or through the Platform does not
              constitute Buygoon&rsquo;s endorsement, recommendation, approval, or warranty of
              that Third-Party Software, the third party that operates it, or any product or
              service accessible through it. Buygoon makes no representations or warranties of any
              kind regarding any Third-Party Software, its availability, reliability, accuracy,
              security, or fitness for any particular purpose.
            </p>

            <h4>13.2 Member&rsquo;s Sole Responsibility</h4>

            <p>
              A Member&rsquo;s use of any Third-Party Software made available on or through the
              Platform is entirely at the Member&rsquo;s own risk and is solely the
              Member&rsquo;s responsibility. The Member&rsquo;s relationship with any third-party
              provider is exclusively between the Member and that third-party provider and is
              governed entirely by that provider&rsquo;s own terms and conditions, privacy policy,
              and any other applicable agreements between the Member and that provider. Buygoon is
              not a party to any such relationship.
            </p>

            <p>
              Buygoon bears no responsibility whatsoever for any Third-Party Software, including
              without limitation for any failure, error, unavailability, security breach, data
              loss, unauthorized access, fee, charge, or any other issue arising from or in
              connection with any Member&rsquo;s use of any Third-Party Software. Members are
              solely responsible for reviewing, understanding, and complying with the terms and
              conditions of any Third-Party Software they use.
            </p>

            <h4>13.3 PayPal</h4>

            <p>
              Without limiting the generality of Sections 13.1 and 13.2, PayPal is a Third-Party
              Software provider. Buygoon&rsquo;s payment obligation is fully discharged upon
              initiating a valid PayPal transfer as described in Section 9.4. All matters arising
              after that moment are entirely between the Member and PayPal, governed by
              PayPal&rsquo;s own terms and privacy policy. Buygoon is not responsible for any
              aspect of PayPal&rsquo;s services, fees, currency handling, account restrictions, or
              any other PayPal matter.
            </p>

            <h4>13.4 Changes to Third-Party Availability</h4>

            <p>
              Buygoon reserves the right to add, remove, modify, or discontinue access to any
              Third-Party Software at any time at its sole and absolute discretion, without notice
              and without liability to any Member.
            </p>

            <h4>13.5 Third-Party Websites and Off-Platform Content</h4>

            <p>
              Once a Member clicks away from the Buygoon Platform to any third-party website
              &mdash; including without limitation any Merchant Retailer website, travel booking
              platform, comparison tool, or any other website or online destination &mdash;
              Buygoon has no control over and bears absolutely no responsibility whatsoever for
              that third-party website or any content, tool, application, software, calculator,
              widget, service, or functionality available on it, regardless of how the Member
              arrived there. This includes without limitation any pricing tool, trip cost
              calculator, booking engine, product configurator, payment processor, or any other
              interactive or non-interactive feature or function on any third-party website.
            </p>

            <p>
              By using the Platform and clicking through to any third-party website, each Member
              expressly and irrevocably waives any and all claims against Buygoon arising out of or
              related to: (a) any third-party website the Member visits after leaving the
              Platform; (b) any content, tool, application, software, or service available on any
              such third-party website; (c) any transaction completed on any such third-party
              website; (d) any loss, damage, or harm of any kind arising from the Member&rsquo;s
              use of any such third-party website or any feature or functionality thereof; or (e)
              any data collected about the Member by any such third-party website or any third
              party operating on that website. Buygoon&rsquo;s responsibility ends at the boundary
              of the Buygoon Platform. Members assume all risk and responsibility for their
              activities on any website they visit after leaving the Platform.
            </p>

            <h2>14. Intellectual Property</h2>

            <h4>14.1 Ownership</h4>

            <p>
              All right, title, and interest in and to the Platform, including without limitation
              all content, features, functionality, software, code, design, graphics, data,
              compilations, and other materials available on or through the Platform (collectively,
              &ldquo;Platform Content&rdquo;), belong to Buygoon Inc. or its licensors. Nothing in
              this Agreement transfers any ownership interest in any Platform Content to any
              Member.
            </p>

            <h4>14.2 Buygoon Marks and Proprietary Terms</h4>

            <p>
              Buygoon Inc. claims and asserts ownership of all right, title, and interest in and
              to its name, logo, trademarks, service marks, and all other marks and branding
              associated with the Buygoon platform (collectively, &ldquo;Buygoon Marks&rdquo;),
              including without limitation the mark &ldquo;Buygoon Bonus Cash,&rdquo; which is a
              proprietary term, defined term, and the exclusive intellectual property of Buygoon
              Inc. As between Buygoon and any Member, the Member expressly acknowledges and agrees
              that &ldquo;Buygoon Bonus Cash&rdquo; and all Buygoon Marks are the intellectual
              property of Buygoon Inc. exclusively, and that the Member acquires no right, title,
              or interest in any Buygoon Mark by virtue of membership or participation in the
              Buygoon Bonus Cash program for any period of time.
            </p>

            <p>
              The Buygoon Marks may not be used in connection with any product or service that is
              not Buygoon&rsquo;s, or in any manner likely to cause confusion among consumers, or
              in any manner that disparages or discredits Buygoon, or that implies a partnership,
              sponsorship, or endorsement by Buygoon that does not exist. Members may not bid on
              any keywords with any search engine that contain any Buygoon Mark, including without
              limitation &ldquo;Buygoon&rdquo; or &ldquo;Buygoon Bonus Cash,&rdquo; without
              Buygoon&rsquo;s prior express written consent. Members may not use any Buygoon Mark
              in any ad text, display advertising, or banner advertising without Buygoon&rsquo;s
              prior express written consent.
            </p>

            <h4>14.3 Referral Network as Intellectual Property</h4>

            <p>
              The four-level Buygoon Bonus Cash referral network architecture &mdash; including
              its structure, mechanics, level configuration, and tracking methodology &mdash; is a
              proprietary platform design and the exclusive intellectual property of Buygoon Inc.
              No Member acquires any intellectual property right in the Referral Network structure
              or any component thereof.
            </p>

            <h4>14.4 Member Licence</h4>

            <p>
              Subject to this Agreement and the Member&rsquo;s continued compliance with all of
              its terms, Buygoon grants each Member a limited, non-exclusive, non-transferable,
              non-sublicensable, revocable licence to access and use the Platform for their
              personal, non-commercial use to participate in the Cashback and Buygoon Bonus Cash
              programs. Members obtain no rights other than those expressly granted in this
              Section. All rights not expressly granted are reserved by Buygoon or its licensors.
            </p>

            <h4>14.5 Prohibited Uses</h4>

            <p>
              Members may not under any circumstances: modify, reverse engineer, decompile, copy,
              or otherwise attempt to derive the source code of any component of the Platform;
              create derivative works based on the Platform or any Platform Content; rent, sell,
              sublicense, or otherwise transfer access to the Platform; use any software, device,
              or process to scrape, crawl, spider, or index any Platform Content; frame or mirror
              any part of the Platform on any other website or application; or otherwise use the
              Platform in any manner not expressly authorized by this Agreement.
            </p>

            <h2>15. Inactive Accounts</h2>

            <h4>15.1 Definition of Inactivity</h4>

            <p>
              An account is considered inactive when the Member has not logged in to their Buygoon
              account for 12 or more consecutive months (&ldquo;Inactive Account&rdquo;). A Member
              need not complete any purchase or initiate any shopping session to maintain active
              status &mdash; a login is sufficient.
            </p>

            <h4>15.2 Inactive Account Maintenance Fee</h4>

            <p>
              On the first day of the 13th consecutive month of inactivity, Buygoon reserves the
              right to begin debiting a maintenance fee of USD $5.00 per month (&ldquo;Maintenance
              Fee&rdquo;) against the Inactive Account&rsquo;s balance, to recover the cost of
              account maintenance in Buygoon&rsquo;s normal course of business. The Maintenance Fee
              continues to be debited monthly until the Member reactivates their account or the
              account balance reaches zero. The Maintenance Fee will never cause an account balance
              to go below zero &mdash; it stops at zero. Maintenance Fees are non-refundable. A
              notice email will be sent to the email address on file at the time the Maintenance
              Fee commences, effective upon sending regardless of receipt.
            </p>

            <h4>15.3 Reactivation</h4>

            <p>
              A Member may reactivate their Inactive Account at any time by logging in.
              Reactivation preserves the then-current account balance only &mdash; being the
              balance remaining after any Maintenance Fees already deducted prior to reactivation.
              Reactivation does not retroactively restore any amounts previously deducted as
              Maintenance Fees. All deducted Maintenance Fees are permanently forfeited upon
              deduction.
            </p>

            <h4>15.4 Account Closure</h4>

            <p>
              Once an Inactive Account balance reaches zero through fee depletion, Buygoon reserves
              the right to permanently close the account and cease to maintain account records and
              Platform access. No further obligation of any kind exists in respect of a permanently
              closed account.
            </p>

            <h2>16. Termination and Account Cancellation</h2>

            <h4>16.1 Buygoon&rsquo;s Right to Terminate or Suspend</h4>

            <p>
              This Agreement is effective upon a Member&rsquo;s acceptance and remains in effect
              until terminated. Buygoon may terminate this Agreement, a Member&rsquo;s account,
              and the Member&rsquo;s access to the Platform at any time, for any reason or no
              reason, including without limitation for any violation of this Agreement, any
              suspected abuse of the Platform, or at Buygoon&rsquo;s sole and absolute discretion.
              Buygoon may, in its sole discretion, at any time and without prior notice,
              discontinue, cancel, suspend, restrict, change, or limit access to all or any part of
              the Platform or any feature thereof. Buygoon will not be liable to any Member or any
              third party for any modification, suspension, or termination of the Platform or any
              Member&rsquo;s access to it.
            </p>

            <h4>16.2 Member&rsquo;s Right to Cancel</h4>

            <p>
              A Member may cancel their account at any time by using the account cancellation
              mechanism available on the Platform.
            </p>

            <p>
              IMPORTANT: ACCOUNT CANCELLATION IS IRREVOCABLE AND CONSTITUTES A VOLUNTARY FORFEITURE
              OF ALL BALANCES. WHEN A MEMBER CANCELS THEIR ACCOUNT THROUGH THE CANCELLATION
              MECHANISM AVAILABLE ON THE PLATFORM, THAT CANCELLATION CONSTITUTES AN IRREVOCABLE
              VOLUNTARY FORFEITURE OF ANY AND ALL BALANCES &mdash; CONFIRMED OR PENDING, CASHBACK
              OR BUYGOON BONUS CASH &mdash; STANDING IN THE MEMBER&rsquo;S ACCOUNT AT THE MOMENT
              CANCELLATION IS EFFECTED. BUYGOON HAS NO FURTHER OBLIGATION OF ANY KIND WITH RESPECT
              TO ANY AMOUNT SHOWN IN THE MEMBER&rsquo;S ACCOUNT AT THE TIME OF CANCELLATION.
              CANCELLATION EFFECTED THROUGH BUYGOON&rsquo;S PLATFORM TECHNOLOGY IS FINAL AND
              BINDING FROM THE MOMENT IT IS COMPLETED.
            </p>

            <h4>16.3 Effect of Termination</h4>

            <p>
              Upon any termination or cancellation, the Member&rsquo;s right to use and access the
              Platform and to earn Cashback or Buygoon Bonus Cash will terminate immediately. All
              provisions of this Agreement that by their nature should survive termination will
              survive, as set out in Section 24.
            </p>

            <h4>16.4 Tax Record Retention</h4>

            <p>
              Notwithstanding any termination or cancellation, Buygoon retains all financial
              records and associated personal information for such period as Buygoon determines,
              in its sole and absolute discretion, to be necessary or appropriate, consistent with
              Section 10.5. While applicable law may establish minimum retention periods,
              Buygoon&rsquo;s right to retain such records is not limited to any statutory minimum
              and extends for as long as Buygoon considers retention to be warranted &mdash; for
              any reason or no reason &mdash; at Buygoon&rsquo;s sole and unfettered discretion.
              This retention obligation survives account cancellation and cannot be overridden by
              account cancellation or any Member deletion request.
            </p>

            <h2>17. Member Feedback</h2>

            <h4>17.1 Feedback Licence</h4>

            <p>
              A Member may from time to time provide Buygoon with feedback, comments, ideas,
              suggestions, reviews, or other information about the Platform (&ldquo;Feedback&rdquo;).
              By providing any Feedback, the Member hereby grants to Buygoon and its affiliates and
              agents a non-exclusive, royalty-free, perpetual, irrevocable, and fully sublicensable
              right to use, reproduce, modify, adapt, publish, perform, translate, create
              derivative works from, distribute, and display the Feedback in any media and for any
              lawful purpose, including without limitation in advertising and promotional materials
              and to enhance or improve Buygoon&rsquo;s products and services. Buygoon is not
              required to treat any Feedback as confidential and is not obligated to compensate a
              Member for any Feedback provided.
            </p>

            <h2>18. Disclaimers and Limitation of Liability</h2>

            <p>
              THE FOLLOWING PROVISIONS CONSTITUTE FUNDAMENTAL AND MATERIAL TERMS OF THIS AGREEMENT
              WITHOUT WHICH BUYGOON WOULD NOT HAVE PROVIDED ACCESS TO THE PLATFORM. THESE
              PROVISIONS APPEAR IN THIS FORMAT TO ENSURE THEY RECEIVE THE PROMINENCE THEY REQUIRE.
              PLEASE READ THEM CAREFULLY.
            </p>

            <h4>18.1 Warranty Disclaimer</h4>

            <p>
              THE PLATFORM, ALL PLATFORM CONTENT, AND ALL THIRD-PARTY SOFTWARE MADE AVAILABLE
              THROUGH THE PLATFORM ARE PROVIDED &ldquo;AS IS&rdquo; AND WITHOUT WARRANTY OF ANY
              KIND, STATUTORY OR OTHERWISE, EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION
              WARRANTIES OF TITLE, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR
              NON-INFRINGEMENT. BUYGOON MAKES NO WARRANTY AS TO THE QUALITY, ACCURACY,
              COMPLETENESS, RELIABILITY, OR VALIDITY OF THE PLATFORM OR ANY PLATFORM CONTENT,
              INCLUDING WITHOUT LIMITATION ANY MERCHANT RETAILER INFORMATION, CASHBACK RATES,
              PRICING INFORMATION, PRODUCT DESCRIPTIONS, OR PRODUCT AVAILABILITY. BUYGOON DOES NOT
              WARRANT THAT THE PLATFORM WILL BE UNINTERRUPTED OR ERROR-FREE, THAT DEFECTS WILL BE
              CORRECTED, OR THAT THE PLATFORM WILL BE FREE OF VIRUSES OR OTHER HARMFUL COMPONENTS.
            </p>

            <h4>18.2 Two-Check Framework Disclaimer</h4>

            <p>
              BUYGOON MAKES NO WARRANTY, REPRESENTATION, OR GUARANTEE THAT ANY PENDING BALANCE WILL
              EVER BECOME A CONFIRMED BALANCE, THAT ANY CONFIRMED BALANCE WILL EVER RESULT IN A
              PAYMENT, OR THAT ANY PARTICULAR CASHBACK RATE OR BUYGOON BONUS CASH RATE WILL REMAIN
              AVAILABLE AT ANY TIME. PENDING BALANCES ARE INFORMATIONAL ONLY AND CREATE NO
              OBLIGATION ON BUYGOON&rsquo;S PART WHATSOEVER.
            </p>

            <h4>18.3 Limitation of Liability</h4>

            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL BUYGOON, OR ANY
              OF ITS CURRENT OR FORMER DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, OR AFFILIATES, BE
              LIABLE FOR ANY SPECIAL, INCIDENTAL, CONSEQUENTIAL, INDIRECT, STATUTORY, EXEMPLARY,
              PUNITIVE, AGGRAVATED, OR OTHER INDIRECT DAMAGES, OR FOR ANY LOSS OF PROFITS, LOSS OF
              DATA, OR LOSS OF USE DAMAGES, EVEN IF BUYGOON HAS BEEN ADVISED OF THE POSSIBILITY OF
              SUCH DAMAGES.
            </p>

            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, EACH MEMBER AGREES THAT THE
              MAXIMUM AGGREGATE LIABILITY OF BUYGOON INC. AND ALL OF ITS CURRENT AND FORMER
              DIRECTORS, OFFICERS, EMPLOYEES, AGENTS, AND AFFILIATES &mdash; INDIVIDUALLY AND
              COLLECTIVELY &mdash; TO ANY INDIVIDUAL MEMBER FOR ANY CLAIM OR CLAIMS OF ANY KIND
              ARISING OUT OF OR RELATED TO THIS AGREEMENT OR THE PLATFORM, REGARDLESS OF THE FORM
              OF ACTION AND REGARDLESS OF THE CIRCUMSTANCES INCLUDING WITHOUT LIMITATION THE
              INSOLVENCY, RECEIVERSHIP, OR BANKRUPTCY OF BUYGOON INC., SHALL NOT EXCEED USD $100.00
              IN THE AGGREGATE.
            </p>

            <p>
              THE USD $100.00 CAP AND THE EXCLUSION OF ALL CONSEQUENTIAL, INDIRECT, PUNITIVE,
              EXEMPLARY, AND AGGRAVATED DAMAGES ARE FUNDAMENTAL TERMS OF THIS AGREEMENT WITHOUT
              WHICH BUYGOON WOULD NOT HAVE PROVIDED ACCESS TO THE PLATFORM.
            </p>

            <h4>18.4 Buygoon&rsquo;s Unrestricted Rights</h4>

            <p>
              For greater certainty, nothing in this Section limits or restricts Buygoon&rsquo;s
              right to seek any and all categories of damages against any Member &mdash; including
              consequential, indirect, punitive, exemplary, and aggravated damages &mdash; without
              limitation. There is no cap on any claim Buygoon may bring against any Member.
            </p>

            <h4>18.5 Directors and Officers</h4>

            <p>
              No current or former director, officer, employee, or agent of Buygoon Inc. bears any
              personal liability to any Member under any circumstances arising out of or related to
              this Agreement, the Platform, or any Member account balance, including without
              limitation in the event of the insolvency, receivership, or bankruptcy of Buygoon
              Inc. In any such circumstances, any and all matters relating to Member accounts pass
              entirely to the receiver, trustee in bankruptcy, or other court-appointed officer,
              and Members&rsquo; recourse, if any, is exclusively against the corporate estate
              through whatever process the applicable insolvency proceedings provide. The USD
              $100.00 aggregate liability cap in Section 18.3 applies equally and in full to all
              individual and collective claims against any current or former director, officer,
              employee, or agent of Buygoon Inc.
            </p>

            <h4>18.6 No Earnings Guarantee &mdash; Results Disclaimer</h4>

            <p>
              Buygoon makes no representation, warranty, promise, or guarantee of any kind
              regarding the amount of Cashback or Buygoon Bonus Cash any Member may earn through
              use of the Platform, whether from their own purchases or from the shopping activity
              of any downstream Referral Network. Participation in the Buygoon platform does not
              guarantee any particular level of income, savings, cashback, or financial benefit of
              any kind.
            </p>

            <p>
              Actual Cashback and Buygoon Bonus Cash earnings vary significantly between Members
              and depend on a wide range of factors entirely outside Buygoon&rsquo;s control,
              including without limitation: the Member&rsquo;s own shopping frequency and volume;
              the shopping frequency and volume of members in the Member&rsquo;s downstream
              Referral Network; the Cashback rates offered by participating Merchant Retailers,
              which are set by those retailers and are subject to change at any time; the size and
              activity level of the Member&rsquo;s Referral Network; and general market and
              economic conditions. Any illustrative examples of Cashback or Buygoon Bonus Cash
              calculations provided in this Agreement or on the Platform &mdash; including the
              examples in Section 7.2 &mdash; are provided for ease of understanding only and do
              not represent typical, average, expected, or guaranteed results.
            </p>

            <p>
              No Member should join or use the Buygoon platform with any expectation of earning
              any particular amount of Cashback or Buygoon Bonus Cash. The earning of any amount
              of Cashback or Buygoon Bonus Cash is contingent on the satisfaction of the Double
              Check Payment Framework and all other conditions set out in this Agreement. Buygoon
              expressly disclaims any liability to any Member who earns less Cashback or Buygoon
              Bonus Cash than they anticipated or expected, for any reason whatsoever.
            </p>

            <h2>19. Indemnification</h2>

            <p>
              To the fullest extent permitted by applicable law, each Member agrees to indemnify,
              defend, and hold harmless Buygoon Inc. and its current and former directors,
              officers, employees, agents, successors, and affiliates from and against any and all
              claims, demands, actions, damages, losses, liabilities, costs, and expenses of any
              kind whatsoever &mdash; including without limitation reasonable legal fees and court
              costs &mdash; arising out of or relating to: (a) the Member&rsquo;s use of or access
              to the Platform; (b) the Member&rsquo;s breach of any provision of this Agreement;
              (c) any misrepresentation made by the Member; (d) any materials provided by the
              Member through or in connection with the Platform; (e) the Member&rsquo;s violation
              of any applicable law or regulation; or (f) the Member&rsquo;s distribution of
              referral links or any public communications about Buygoon.
            </p>

            <p>
              This indemnification obligation is one-directional only &mdash; it runs exclusively
              from the Member to Buygoon. Buygoon provides no indemnification to any Member under
              any circumstances whatsoever. This indemnification obligation is unlimited and
              uncapped and is not subject to the USD $100.00 liability cap in Section 18.3, which
              applies only to claims by Members against Buygoon. The Member agrees to cooperate as
              fully as reasonably required in Buygoon&rsquo;s defence and/or settlement of any
              claim. Buygoon reserves the right, in its reasonable discretion, to assume exclusive
              control over the defence and settlement of any matter subject to indemnification by
              the Member.
            </p>

            <h2>20. Business Transfers</h2>

            <p>
              Buygoon may transfer, assign, novate, or otherwise dispose of this Agreement and all
              Member agreements &mdash; in whole or in part &mdash; in connection with any merger,
              acquisition, sale of assets, reorganization, financing, change of control, or other
              disposal of all or part of Buygoon&rsquo;s business or assets, without the consent of
              any Member. Members consent to any such transfer as a condition of using the
              Platform. Any transferee steps into Buygoon&rsquo;s position under this Agreement
              and is bound by its terms with respect to all Member accounts. The existence of any
              unwithdrawn Member account balances creates no restriction, encumbrance, or
              liability on Buygoon&rsquo;s ability to enter into any such corporate transaction,
              consistent with Section 9.8.
            </p>

            <h2>21. Changes to Terms</h2>

            <p>
              Buygoon may, at its sole discretion, modify, update, add to, discontinue, remove, or
              otherwise change these Terms and Conditions at any time. Each modification will take
              effect upon notification to Members. Buygoon will provide notice of material changes
              by sending an email to the address on file, effective immediately upon sending.
              Members are responsible for maintaining a current email address as required by
              Section 5.2. The most current version of these Terms and Conditions will be available
              on the Platform and supersedes all previous versions. A Member&rsquo;s continued use
              of the Platform following any notification of changes constitutes that Member&rsquo;s
              acceptance of the modified Terms. If a Member does not agree to any modification, the
              Member&rsquo;s sole remedy is to cancel their account &mdash; subject to the balance
              forfeiture provisions in Section 16.2. For the avoidance of doubt, a Member&rsquo;s
              continued use of the Buygoon Platform following any change to these Terms and
              Conditions &mdash; whether or not the Member has read or been notified of the
              updated Terms &mdash; constitutes that Member&rsquo;s deemed acceptance of and
              agreement to the revised Terms and Conditions in their entirety, effective as of the
              date of such continued use.
            </p>

            <h2>22. Dispute Resolution and Governing Law</h2>

            <p>
              THIS SECTION CONTAINS PROVISIONS THAT SIGNIFICANTLY AFFECT YOUR LEGAL RIGHTS,
              INCLUDING THE RIGHT TO MAINTAIN A COURT ACTION, THE RIGHT TO PARTICIPATE IN ANY
              CLASS ACTION OR REPRESENTATIVE PROCEEDING, AND THE SCOPE OF REMEDIES AVAILABLE TO
              YOU. PLEASE READ THIS SECTION IN ITS ENTIRETY BEFORE ACCEPTING THESE TERMS.
            </p>

            <h4>22.1 Factual Recital &mdash; Nature of the Relationship</h4>

            <p>
              Members acknowledge and agree to the following facts, which form the foundation of
              the dispute resolution framework in this Section:
            </p>

            <ul>
              <li>
                Buygoon is a performance marketing intermediary and affiliate rewards platform. No
                goods or services are purchased from Buygoon by any Member.
              </li>
              <li>
                No monetary consideration of any kind passes from any Member to Buygoon at any
                time.
              </li>
              <li>
                Membership in the Buygoon Platform is conditioned solely on consent to the tracking
                technologies described in the Privacy Policy, provided through the Cookie Wall.
              </li>
              <li>
                The Cashback and Buygoon Bonus Cash credited to Member accounts represent
                Contingent Rights to Payment &mdash; a conditional personal right to receive a
                portion of Affiliate Commissions earned by Buygoon from third-party Merchant
                Retailers, subject to the Double Check Payment Framework.
              </li>
              <li>
                In light of the foregoing, the parties agree that the relationship between Buygoon
                and its Members is not a consumer agreement within the meaning of the Ontario
                Consumer Protection Act, 2002 or any successor legislation.
              </li>
            </ul>

            <h4>22.2 Governing Law</h4>

            <p>
              This Agreement and all matters arising out of or relating to it shall be governed by
              and construed in accordance with the laws of the Province of Ontario and the
              applicable federal laws of Canada, without regard to conflict of law principles of
              any jurisdiction whatsoever.
            </p>

            <h4>22.3 Buygoon&rsquo;s Unconstrained Legal Rights</h4>

            <p>
              Buygoon may seek any legal remedy &mdash; including without limitation injunctive
              relief, emergency arbitration under ADRIC&rsquo;s urgent interim measures provisions,
              or any other legal action in any court of competent jurisdiction &mdash; at any
              time, without satisfying any pre-dispute condition, and without prior notice to the
              Member where circumstances require immediate action. Nothing in this Section
              restricts or conditions Buygoon&rsquo;s right to seek any remedy at any time.
            </p>

            <h4>22.4 Mandatory Pre-Dispute Notice Requirements &mdash; Members Only</h4>

            <p>
              The following pre-dispute notice requirements apply exclusively to Members. Buygoon
              is not subject to these requirements. A Member wishing to assert any claim against
              Buygoon must first deliver written notice of that claim in strict compliance with
              the dual delivery requirements below. Notice is valid and effective &mdash; and no
              time period begins to run &mdash; unless and until BOTH of the following deliveries
              have been completed and confirmed:
            </p>

            <ul>
              <li>
                Written notice sent by email to{' '}
                <a href="mailto:legal@buygoon.com">legal@buygoon.com</a>; AND
              </li>
              <li>
                A hard copy of the identical notice delivered by prepaid courier (such as FedEx,
                UPS, or Purolator) to Buygoon Inc. at 342 Queen St W, Suite 200, Toronto, ON M5V
                2A2, with delivery confirmation.
              </li>
            </ul>

            <p>
              Written notice must be delivered within 30 days of the date the Member first knew or
              ought reasonably to have known of the facts giving rise to the claim. Notice must
              describe the nature of the claim and the remedy sought in reasonable detail. No time
              period under this Section shall commence, and no notice shall be deemed valid or
              effective, until actual confirmed receipt of both deliveries has been established.
              If either delivery is missing or unconfirmed, the notice is defective and no time
              period runs.
            </p>

            <h4>22.5 Buygoon&rsquo;s Response Window</h4>

            <p>
              Buygoon has 90 days from actual confirmed receipt of a valid dual notice to respond.
              Buygoon has no obligation to respond within that period and may respond at any time
              entirely at its election. After 90 days from confirmed receipt of valid dual notice,
              the Member&rsquo;s claim ripens &mdash; but the Member may not immediately file for
              arbitration.
            </p>

            <h4>22.6 Mandatory Settlement Discussion</h4>

            <p>
              After the Member&rsquo;s claim ripens, Buygoon has a 30-day window (being days 90 to
              120 from confirmed receipt of valid dual notice) to invoke its mandatory settlement
              discussion right. If invoked, Buygoon has sole discretion to require either
              in-person discussions at any location within the Greater Toronto Area (defined for
              purposes of this Section as any location within a linear radius of 75 kilometres of
              Toronto City Hall, at Buygoon&rsquo;s sole discretion as to specific location), or
              videoconference discussions. The Member must engage meaningfully for a reasonable
              period as determined by Buygoon in the circumstances. The Member may not file for
              arbitration during any period in which a mandatory settlement discussion has been
              invoked and is ongoing.
            </p>

            <p>Complete pre-arbitration sequencing:</p>

            <ul>
              <li>
                Day 1: Member delivers valid dual notice &mdash; email to{' '}
                <a href="mailto:legal@buygoon.com">legal@buygoon.com</a> AND prepaid courier to
                342 Queen St W, Suite 200, Toronto, ON M5V 2A2 &mdash; both confirmed received.
                The applicable time period commences upon confirmed receipt of the later of the
                two deliveries.
              </li>
              <li>Days 1&ndash;90: Buygoon&rsquo;s response window. Buygoon has no obligation to respond.</li>
              <li>Day 90: Member&rsquo;s claim ripens. Member may not yet file for arbitration.</li>
              <li>
                Days 90&ndash;120: Buygoon&rsquo;s 30-day window to invoke mandatory settlement
                discussion right.
              </li>
              <li>
                If invoked: discussions proceed for a reasonable period as Buygoon determines.
                Member must engage meaningfully. Member may not file during this period.
              </li>
              <li>
                If not invoked within 30 days of ripening: Member may file for arbitration under
                Section 22.7.
              </li>
              <li>
                If discussions conclude without resolution: Member may file for arbitration under
                Section 22.7.
              </li>
            </ul>

            <h4>22.7 Binding Arbitration</h4>

            <p>
              Subject to satisfaction of all pre-dispute conditions in Sections 22.4 through 22.6,
              any dispute, claim, or controversy arising out of or relating to this Agreement,
              Buygoon&rsquo;s business, the Platform, or the Member&rsquo;s relationship with
              Buygoon &mdash; including any claims against Buygoon&rsquo;s directors, officers,
              employees, agents, or affiliates &mdash; shall be resolved by binding and final
              arbitration administered by the ADR Institute of Canada (&ldquo;ADRIC&rdquo;)
              pursuant to ADRIC&rsquo;s 2025 Arbitration Rules (the &ldquo;ADRIC Rules&rdquo;),
              available at www.adric.ca.
            </p>

            <ul>
              <li>
                <b>Arbitrator:</b> A single arbitrator appointed in accordance with the ADRIC Rules.
              </li>
              <li>
                <b>Seat and Venue:</b> Toronto, Ontario, Canada.
              </li>
              <li>
                <b>Format:</b> In person. No remote hearings.
              </li>
              <li>
                <b>Governing Law:</b> The laws of the Province of Ontario and applicable federal
                laws of Canada, without regard to conflict of law principles of any jurisdiction
                whatsoever.
              </li>
              <li>
                <b>Language:</b> English.
              </li>
            </ul>

            <p>
              The arbitrator&rsquo;s decision shall be final and binding upon the parties and may
              be enforced in any court of competent jurisdiction.
            </p>

            <h4>22.8 Class Action Waiver</h4>

            <p>
              MEMBERS EXPRESSLY AND IRREVOCABLY WAIVE ANY AND ALL RIGHTS TO COMMENCE, JOIN, OR
              PARTICIPATE IN ANY CLASS ACTION, CLASS ARBITRATION, COLLECTIVE ACTION, OR
              REPRESENTATIVE PROCEEDING OF ANY KIND ARISING OUT OF OR RELATED TO THIS AGREEMENT OR
              THE BUYGOON PLATFORM, WHETHER IN COURT OR IN ARBITRATION. MEMBERS EXPRESSLY
              ACKNOWLEDGE AND ADMIT THAT THEY HAVE NO STANDING TO BRING OR PARTICIPATE IN ANY SUCH
              PROCEEDING. THE ARBITRATOR HAS NO AUTHORITY TO CONSOLIDATE CLAIMS, CONDUCT CLASS
              ARBITRATION, OR AWARD RELIEF TO ANY PERSON OTHER THAN THE INDIVIDUAL MEMBER. THIS
              CLASS ACTION WAIVER IS A FUNDAMENTAL, MATERIAL, AND NON-SEVERABLE TERM OF THIS
              AGREEMENT WITHOUT WHICH BUYGOON WOULD NOT HAVE PROVIDED ACCESS TO THE PLATFORM. IF
              THIS CLASS ACTION WAIVER IS FOUND UNENFORCEABLE BY ANY COURT OR TRIBUNAL, THE ENTIRE
              ARBITRATION AGREEMENT IN THIS SECTION IS VOID AND ANY DISPUTE MUST BE RESOLVED IN
              THE COURTS OF ONTARIO ON AN INDIVIDUAL BASIS ONLY &mdash; ELIMINATING ANY POSSIBILITY
              OF CLASS ARBITRATION.
            </p>

            <h4>22.9 Consumer Agreement Fallback</h4>

            <p>
              If the dispute resolution framework in this Section &mdash; including the arbitration
              clause and class action waiver &mdash; is found unenforceable by operation of the
              Ontario Consumer Protection Act, 2002 or any other applicable law, the following
              fallback applies:
            </p>

            <ul>
              <li>
                <b>Governing law:</b> Province of Ontario, Canada, without regard to conflict of
                law principles of any jurisdiction whatsoever.
              </li>
              <li>
                <b>Exclusive jurisdiction:</b> The Ontario Superior Court of Justice, Commercial
                List, Toronto, Ontario.
              </li>
              <li>
                Members irrevocably submit to that jurisdiction and waive any objection to venue or
                forum.
              </li>
              <li>
                All Member rights and remedies remain subject to the same pre-litigation conditions
                precedent as the arbitration framework &mdash; the 30-day dual notice requirement,
                the 90-day Buygoon response window, and Buygoon&rsquo;s right to invoke settlement
                discussions for a reasonable period.
              </li>
            </ul>

            <p>
              This fallback is expressly connected to and triggered by the failure of the primary
              arbitration framework. It is not a freestanding provision and does not apply unless
              and until the primary arbitration framework is found unenforceable.
            </p>

            <h2>23. General Provisions</h2>

            <h4>23.1 Entire Agreement</h4>

            <p>
              These Terms and Conditions, together with Buygoon&rsquo;s Privacy Policy (available
              at <a href="/privacy-policy">www.buygoon.com/privacy-policy</a>), constitute the
              entire agreement between each Member and Buygoon and govern the Member&rsquo;s use
              of the Platform, superseding any and all prior agreements, representations,
              statements, or understandings between the Member and Buygoon with respect to the
              Platform. Any representations, statements, or agreements made or entered into
              elsewhere, whether directly or indirectly, written or oral or in advertising, are
              not binding on Buygoon unless expressly confirmed in writing by Buygoon.
            </p>

            <h4>23.2 Severability</h4>

            <p>
              If any provision of this Agreement is held to be invalid, illegal, or unenforceable
              by any court or tribunal of competent jurisdiction, such invalidity shall not affect
              any other provision of this Agreement, which shall remain in full force and effect,
              except as otherwise expressly stated with respect to the class action waiver and
              arbitration agreement in Section 22.
            </p>

            <h4>23.3 Waiver</h4>

            <p>
              Any waiver or failure by Buygoon to enforce any provision of this Agreement on any
              occasion will not be deemed a waiver of any other provision or of such provision on
              any other occasion. No waiver by Buygoon of any right or remedy is effective unless
              recorded in a writing signed by an authorized representative of Buygoon.
            </p>

            <h4>23.4 Assignment</h4>

            <p>
              A Member may not assign, transfer, or otherwise dispose of their rights and
              obligations under this Agreement, in whole or in part, without Buygoon&rsquo;s prior
              written consent, and any purported assignment without such consent is null and void.
              Buygoon has the right to transfer, assign, or otherwise dispose of this Agreement
              and all Member agreements without any Member&rsquo;s consent, consistent with
              Section 20.
            </p>

            <h4>23.5 Notices</h4>

            <p>
              Formal legal notices to Buygoon under this Agreement must be delivered in compliance
              with the dual delivery requirements in Section 22.4 &mdash; by email to{' '}
              <a href="mailto:legal@buygoon.com">legal@buygoon.com</a> AND by prepaid courier to
              Buygoon Inc. at 342 Queen St W, Suite 200, Toronto, ON M5V 2A2. Privacy inquiries
              must be directed to <a href="mailto:privacy@buygoon.com">privacy@buygoon.com</a>,
              which is not a valid address for legal notices and is not interchangeable with{' '}
              <a href="mailto:legal@buygoon.com">legal@buygoon.com</a>. Notices from Buygoon to
              Members are validly given by email to the address on file, by posting on the
              Platform, or by any other reasonable means. Notice by email is effective upon
              sending.
            </p>

            <h4>23.6 Interpretation</h4>

            <p>
              Headings in this Agreement are for convenience only and do not affect interpretation.
              The word &ldquo;including&rdquo; means &ldquo;including without limitation.&rdquo;
              References to sections are to sections of this Agreement. This Agreement shall not
              be construed against either party solely by reason of the fact that it was drafted by
              or on behalf of that party.
            </p>

            <h4>23.7 Community Standards</h4>

            <p>
              By participating in the Platform, Members become part of a community that depends on
              the goodwill and responsible behaviour of each Member. Members must refrain from
              transmitting or communicating images or text constituting ethnic slurs, obscenities,
              sexually explicit material, inflammatory or derogatory comments, or anything else
              that may be construed as harassing or offensive, targeted at the Platform,
              Buygoon&rsquo;s employees, contractors or agents, Merchant Retailers, or other
              Members. Members who violate this provision, as determined by Buygoon in its sole
              discretion, may have their access to the Platform suspended or terminated without
              prior notice.
            </p>

            <h4>23.8 Relationship of the Parties</h4>

            <p>
              Nothing in this Agreement will be construed as constituting, creating, or implying
              any agency, employment, franchise, partnership, joint venture, or any other form of
              legal or commercial relationship between Buygoon and any Member. No Member is an
              employee, agent, franchisee, partner, or representative of Buygoon for any purpose
              whatsoever. No Member has the right, authority, or power to bind Buygoon to any
              agreement with any third party, to incur any obligation or liability on behalf of
              Buygoon, or to hold themselves out as having any such authority. Buygoon is not the
              employer, franchisor, partner, or principal of any Member. This provision applies
              regardless of the nature or extent of any Member&rsquo;s participation in the
              Buygoon Bonus Cash referral program or the size of any Member&rsquo;s downstream
              Referral Network.
            </p>

            <h2>24. Survival</h2>

            <p>
              Any and all provisions of this Agreement which by their very nature and/or apparent
              intention would survive the termination, cancellation, or expiration of this
              Agreement shall so survive, together with the following provisions, which shall
              expressly survive any termination, cancellation, or expiration of this Agreement and
              continue in full force and effect after the Member&rsquo;s account is closed for any
              reason:
            </p>

            <ul>
              <li>
                Section 9 (Payment Terms) &mdash; including the Double Check Payment Framework,
                clawback rights, non-transferability and creditor protection, death of a Member,
                and corporate transactions
              </li>
              <li>
                Section 10 (Tax Reporting and Member Tax Obligations) &mdash; including the
                seven-year record retention obligation
              </li>
              <li>
                Section 11 (Prohibited Conduct) &mdash; including consequences of breach and the
                conspiracy and collusion carve-out
              </li>
              <li>
                Section 14 (Intellectual Property) &mdash; including Buygoon&rsquo;s ownership of
                all Platform Content and Buygoon Marks
              </li>
              <li>
                Section 7.6 (Ownership of the Referral Network) &mdash; Buygoon&rsquo;s ownership
                of the Referral Network survives indefinitely
              </li>
              <li>
                Section 16 (Termination and Account Cancellation) &mdash; including balance
                forfeiture provisions
              </li>
              <li>
                Section 18 (Disclaimers and Limitation of Liability) &mdash; including the USD
                $100.00 aggregate liability cap and all damages exclusions
              </li>
              <li>
                Section 19 (Indemnification) &mdash; Member&rsquo;s one-way, uncapped
                indemnification obligation survives indefinitely
              </li>
              <li>
                Section 22 (Dispute Resolution and Governing Law) &mdash; including pre-dispute
                notice requirements, mandatory settlement discussion right, ADRIC arbitration,
                class action waiver, and governing law
              </li>
              <li>Section 23 (General Provisions)</li>
              <li>
                All warranties, representations, and eligibility requirements made by Members at
                registration &mdash; including age, jurisdiction, personal capacity, and human
                operation representations &mdash; survive and remain actionable after termination
              </li>
            </ul>

            <h2>25. Force Majeure</h2>

            <p>
              Buygoon will not be in breach of this Agreement, and will have no liability to any
              Member of any kind, by reason of any partial or total failure or delay in performance
              of any of its obligations under this Agreement where such failure or delay is caused
              by or results from circumstances beyond Buygoon&rsquo;s reasonable control (each, a
              &ldquo;Force Majeure Event&rdquo;), including without limitation: any public health
              emergency, epidemic, or pandemic; any law, by-law, rule, regulation, order,
              directive, public guidance, restriction, or other action by any governmental,
              regulatory, public health, or law enforcement authority; act of God; fire; flood;
              earthquake; storm; lightning; explosion; natural disaster; war; act of terrorism;
              civil commotion; riot; public protest; sabotage; strikes or labour disputes;
              inability to obtain necessary materials, equipment, or services; interruption or
              failure of utility services or telecommunications infrastructure; failure or
              unavailability of any Affiliate Network Partner&rsquo;s systems or services; failure
              or unavailability of PayPal&rsquo;s systems or services; or any other cause beyond
              Buygoon&rsquo;s reasonable control, whether or not similar to the foregoing.
            </p>

            <p>
              During any Force Majeure Event: (a) Buygoon&rsquo;s obligations under this Agreement
              &mdash; including without limitation any obligation to credit Cashback or Buygoon
              Bonus Cash, to process withdrawal requests, or to initiate PayPal transfers &mdash;
              are suspended for the duration of the Force Majeure Event; (b) the Double Check
              Payment Framework continues to apply and no payment obligation crystallizes during a
              period of suspension; and (c) no Member acquires any claim against Buygoon arising
              out of or related to Buygoon&rsquo;s non-performance during the Force Majeure Event.
              Buygoon will use commercially reasonable efforts to resume performance as soon as
              reasonably practicable following the cessation of the Force Majeure Event and will
              provide notice to Members via the Platform or email to the address on file when
              normal operations resume. This Force Majeure provision applies exclusively to
              Buygoon. Members remain bound by all of their obligations under this Agreement
              regardless of any event or circumstance affecting the Member.
            </p>

            <p>
              <em>Buygoon Inc. &mdash; Terms and Conditions v1.5 &mdash; Effective May 17, 2026</em>
              <br />
              <em>Governed by the laws of the Province of Ontario, Canada</em>
              <br />
              <em>
                <a href="mailto:legal@buygoon.com">legal@buygoon.com</a> &mdash; www.buygoon.com
              </em>
            </p>
          </div>
        </Col>
      </Row>
    </Layout>
  )
}

export default TermsAndConditions
